import React, { FC, useContext, useEffect, useState } from 'react';
import { X } from 'react-feather';
import { Grid, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ErrorMessage, useToastErrorMessage } from '@/utils/Messages';
import { compareList } from '@/utils/Sorting';

import { getOtas, getProperties } from '@/services/Property/request';
import { Property, RequestOta } from '@/services/Property/types';
import { putReservationReallocate } from '@/services/Reservation/request';
import { ReservationReallocateProps } from '@/services/Reservation/types';

import { useLoader } from '@/context/LoaderContext';
import { useToast } from '@/context/ToastContext';

import { CalendarContext } from '@/components/Calendar/Calendar';
import DropdownAutocomplete, {
  SelectProps,
} from '@/components/DropdownAutocomplete/DropdownAutocomplete';
import FormButton from '@/components/FormButton';
import FormGridRow from '@/components/FormGridRow';
import SimpleSelect from '@/components/SimpleSelect';
import { SelectOption } from '@/components/SimpleSelect/SimpleSelect';

import {
  ButtonsContainer,
  CloseButtonContainer,
  Form,
  ModalBox,
  RoundedDialog,
  Title,
  TitleContainer,
} from './styles';

interface PropsClose {
  onClose: Function;
  open: boolean;
  reservationId: number;
}

const PAGE = 1;
const PAGE_SIZE = 1000;

const Transition = React.forwardRef(
  (
    props:
      | (TransitionProps & {
          children?: React.ReactElement<any, any>;
        })
      | any,
    ref: React.Ref<unknown>,
  ) => <Slide direction="up" ref={ref} {...props} />,
);

const ModalReallocateReservation: FC<PropsClose> = ({
  open = true,
  onClose = () => {},
  reservationId,
}) => {
  const { getReservation } = useContext(CalendarContext);
  const { setLoad } = useLoader();
  const toast = useToast();
  const toastErrorRequest = useToastErrorMessage();
  const [properties, setProperties] = useState<Property[]>([]);
  const [otas, setOtas] = useState<RequestOta[]>([]);

  const handleGetProperties = async () => {
    try {
      const response = await getProperties(PAGE, PAGE_SIZE);
      setProperties(response.results);
    } catch (e: unknown) {
      if (e instanceof Error) {
        toast.error(e.message || ErrorMessage.default());
        toastErrorRequest(e);
      }
    }
  };

  const handleGetOtas = async () => {
    try {
      const response = await getOtas();
      setOtas(response);
    } catch (e: unknown) {
      if (e instanceof Error) {
        toast.error(e.message || ErrorMessage.default());
        toastErrorRequest(e);
      }
    }
  };

  useEffect(() => {
    if (open) {
      handleGetProperties();
      handleGetOtas();
    }
  }, [open]);

  const handleGetProperty = (): SelectProps[] =>
    properties
      .map((property) => ({ optionText: `${property.code}`, optionValue: `${property.id}` }))
      .sort((a, b) => compareList(a.optionText, b.optionText));

  const handleClose = () => {
    onClose(false);
  };

  const validation = Yup.object().shape({
    ota: Yup.number().required('Selecione a OTA'),
    property_id: Yup.number().required('Selecione o imóvel'),
  });

  const formik = useFormik({
    initialValues: {
      ota: undefined,
      property_id: undefined,
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      try {
        setLoad(true);
        const data: ReservationReallocateProps = {
          reservation: Number(reservationId),
          ota: Number(values.ota),
          property: Number(values.property_id),
        };

        await putReservationReallocate(data);
        await getReservation();
        setLoad(false);
        handleClose();
        toast.success('Reserva realocada com sucesso!');
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast.error(e.message || ErrorMessage.default());
          toastErrorRequest(e);
          setLoad(false);
        }
      }
    },
  });

  return (
    <RoundedDialog TransitionComponent={Transition} keepMounted open={open} onClose={handleClose}>
      <ModalBox>
        <TitleContainer>
          <Title>Realocar reserva</Title>
          <CloseButtonContainer>
            <X size={20} onClick={handleClose} />
          </CloseButtonContainer>
        </TitleContainer>

        <Form onSubmit={formik.handleSubmit} onContextMenu={(e) => e.preventDefault()}>
          <FormGridRow grid>
            <Grid item sm={12} xs={12}>
              <SimpleSelect
                id="ota"
                dataCy="ota"
                placeholder="Selecione"
                label="OTA - Origem da reserva"
                required
                formik={formik}
                options={(otas || []).map<SelectOption>(({ id, name }) => ({
                  value: id,
                  valueLabel: name,
                }))}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <DropdownAutocomplete
                dataCy="select-properties-list"
                label="Imóvel: (Pesquise pelo código do imóvel)"
                id="property_id"
                required
                options={handleGetProperty()}
                formik={formik}
              />
            </Grid>
          </FormGridRow>
          <ButtonsContainer>
            <FormButton
              dataCy="btn-cancel-reallocate-reservation"
              variant="outlined"
              onClick={handleClose}
            >
              Cancelar
            </FormButton>
            <FormButton dataCy="btn-confirm-reallocate-reservation" type="submit">
              Confirmar
            </FormButton>
          </ButtonsContainer>
        </Form>
      </ModalBox>
    </RoundedDialog>
  );
};

export default ModalReallocateReservation;
